/**
 * Moist-air arithmetic: saturation vapour pressure, dew point, frost point, relative
 * humidity (from dew point or from a psychrometer) and absolute humidity.
 *
 * Saturation vapour pressure uses the improved Magnus form of Alduchov & Eskridge (1996),
 * J. Appl. Meteorol. 35(4), 601–609:
 *
 *   es(t) = a · exp(b·t / (c + t))      (t in °C, es in hPa)
 *   over water: a = 6.1094, b = 17.625, c = 243.04   (fitted −40 to 60 °C, error ≈ 0.4 %)
 *   over ice:   a = 6.1121, b = 22.587, c = 273.86   (fitted −80 to 0 °C)
 *
 * Inverting for the temperature at which a given vapour pressure e is saturated gives
 *   t = c · ln(e/a) / (b − ln(e/a)),
 * which is the dew point (water constants) or the frost point (ice constants).
 */

export interface MagnusConstants {
  /** Saturation vapour pressure at 0 °C, hPa. */
  a: number;
  b: number;
  /** °C. */
  c: number;
}

export const MAGNUS_WATER: MagnusConstants = {
  a: 6.1094,
  b: 17.625,
  c: 243.04,
};
export const MAGNUS_ICE: MagnusConstants = { a: 6.1121, b: 22.587, c: 273.86 };

/** The range of the water fit; outside it the result is a rougher estimate. */
export const MAGNUS_WATER_RANGE_C = { min: -40, max: 60 } as const;

export type Phase = "water" | "ice";

const CONSTANTS: Record<Phase, MagnusConstants> = {
  water: MAGNUS_WATER,
  ice: MAGNUS_ICE,
};

/** Saturation vapour pressure in hPa at `celsius`, over water or ice. */
export function saturationVaporPressure(
  celsius: number,
  phase: Phase = "water"
): number {
  const { a, b, c } = CONSTANTS[phase];
  return a * Math.exp((b * celsius) / (c + celsius));
}

/** Actual vapour pressure in hPa for a relative humidity (percent, over water). */
export function vaporPressure(
  celsius: number,
  humidityPercent: number
): number {
  return (humidityPercent / 100) * saturationVaporPressure(celsius, "water");
}

/** The temperature (°C) at which vapour pressure `e` (hPa) saturates the air. */
export function saturationTemperature(e: number, phase: Phase): number {
  const { a, b, c } = CONSTANTS[phase];
  const gamma = Math.log(e / a);
  return (c * gamma) / (b - gamma);
}

/** Dew point in °C from air temperature (°C) and relative humidity (% over 0). */
export function dewPoint(celsius: number, humidityPercent: number): number {
  return saturationTemperature(
    vaporPressure(celsius, humidityPercent),
    "water"
  );
}

/** Frost point in °C — the dew point's counterpart over ice. */
export function frostPoint(celsius: number, humidityPercent: number): number {
  return saturationTemperature(vaporPressure(celsius, humidityPercent), "ice");
}

/** Relative humidity (%) from air temperature and dew point, both °C. */
export function relativeHumidityFromDewPoint(
  celsius: number,
  dewPointCelsius: number
): number {
  return (
    (100 * saturationVaporPressure(dewPointCelsius, "water")) /
    saturationVaporPressure(celsius, "water")
  );
}

/** Specific gas constant of water vapour, J/(kg·K). */
export const WATER_VAPOR_GAS_CONSTANT = 461.5;

/** Absolute humidity in g/m³ from vapour pressure (hPa) and temperature (°C). */
export function absoluteHumidity(e: number, celsius: number): number {
  return (e * 100 * 1000) / (WATER_VAPOR_GAS_CONSTANT * (celsius + 273.15));
}

/**
 * The psychrometer equation of Ferrel, as summarised in the Smithsonian Meteorological
 * Tables and the AMS Glossary, for an adequately ventilated psychrometer:
 *
 *   e = es(Tw) − K · (1 + 0.00115 Tw) · P · (T − Tw)
 *
 * with temperatures in °C and e, es and P in the same pressure unit. K is 6.60 × 10⁻⁴ per
 * kelvin for a water-covered wet bulb and about 5.82 × 10⁻⁴ for an ice-covered one, where
 * es(Tw) is then the saturation pressure over ice. An unventilated wet bulb needs a larger
 * K (the air around it is not renewed), so the formula understates humidity there.
 */
export const PSYCHROMETER_K = { water: 6.6e-4, ice: 5.82e-4 } as const;
export const PSYCHROMETER_TEMPERATURE_FACTOR = 0.001_15;
/** Standard sea-level pressure, hPa. */
export const STANDARD_PRESSURE_HPA = 1013.25;

/** Vapour pressure (hPa) from dry- and wet-bulb readings (°C) at station pressure `pressureHpa`. */
export function vaporPressureFromWetBulb(
  dryBulb: number,
  wetBulb: number,
  pressureHpa: number,
  iceBulb = false
): number {
  const phase: Phase = iceBulb ? "ice" : "water";
  const k = PSYCHROMETER_K[phase];
  return (
    saturationVaporPressure(wetBulb, phase) -
    k *
      (1 + PSYCHROMETER_TEMPERATURE_FACTOR * wetBulb) *
      pressureHpa *
      (dryBulb - wetBulb)
  );
}

/** Relative humidity (%) from vapour pressure e (hPa) at the dry-bulb temperature (°C). */
export function relativeHumidityFromVaporPressure(
  celsius: number,
  e: number
): number {
  return (100 * e) / saturationVaporPressure(celsius, "water");
}
